import { useEffect, useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import { LabeledInput, TextArea, TextField } from "@/ui/core";
import type { LabeledInputProps } from "@/ui/core";
import { Box } from "@/ui/layout";

const StoryMeta: Meta<LabeledInputProps> = {
  title: "Core/Labeled Input",
  component: LabeledInput,
  argTypes: {
    hasError: { control: "boolean" },
    isRequired: { control: "boolean" },
    leftText: { control: "text" },
    rightText: { control: "text" },
  },
  args: {
    hasError: false,
    isRequired: false,
    leftText: "Email address",
    testID: "labeled-input",
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const [value, setValue] = useState("");

    useEffect(() => {
      setValue("");
    }, [args.hasError]);

    return (
      <Box width="100%">
        <LabeledInput
          hasError={args.hasError}
          isRequired={args.isRequired}
          leftText={args.leftText}
          rightText={args.rightText}
          testID={args.testID}
        >
          <TextField
            hasError={args.hasError}
            onChange={setValue}
            placeholder="Enter email"
            testID="email-field"
            value={value}
          />
        </LabeledInput>
      </Box>
    );
  },
};

type Story = StoryObj<{ hasError?: boolean } & LabeledInputProps>;

const Primary: Story = {};

const Required: Story = {
  args: {
    isRequired: true,
    leftText: "Full name",
  },
};

const WithError: Story = {
  args: {
    hasError: true,
    isRequired: true,
    leftText: "Email address",
  },
};

const WithSecondaryText: Story = {
  args: {
    leftText: "Phone number",
    rightText: "optional",
  },
};

const WithTextArea: Story = {
  args: {
    isRequired: true,
    leftText: "Your why",
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const [value, setValue] = useState("");

    return (
      <Box width="100%">
        <LabeledInput
          isRequired={args.isRequired}
          leftText={args.leftText}
          rightText={args.rightText}
          testID={args.testID}
        >
          <TextArea
            maxLength={500}
            onChange={setValue}
            placeholder="What motivates you on this journey?"
            testID="your-why-field"
            value={value}
          />
        </LabeledInput>
      </Box>
    );
  },
};

const WithTextAreaError: Story = {
  args: {
    isRequired: true,
    leftText: "Your why",
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const [value, setValue] = useState("");

    return (
      <Box width="100%">
        <LabeledInput
          isRequired={args.isRequired}
          leftText={args.leftText}
          rightText={args.rightText}
          testID={args.testID}
        >
          <TextArea
            errorMessage="This field is required"
            hasError
            maxLength={500}
            onChange={setValue}
            placeholder="What motivates you on this journey?"
            testID="your-why-field"
            value={value}
          />
        </LabeledInput>
      </Box>
    );
  },
};

const multiSelectOptions: MultiSelectOption[] = [
  { displayText: "Anxiety", value: "anxiety" },
  { displayText: "Depression", value: "depression" },
  { displayText: "Eating disorder", value: "eating-disorder" },
  { displayText: "ADHD", value: "adhd" },
];

const WithMultiSelect: Story = {
  args: {
    leftText: "Conditions",
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const [selected, setSelected] = useState<string[]>([]);

    return (
      <Box width="100%">
        <LabeledInput
          hasError={args.hasError}
          isRequired={args.isRequired}
          leftText={args.leftText}
          rightText={args.rightText}
          testID={args.testID}
        >
          <MultiSelectTextField
            hasError={args.hasError}
            modalTitle="Select conditions"
            onValuesChange={(options) =>
              setSelected(options.map((o) => o.displayText))
            }
            options={multiSelectOptions}
            testID="conditions-field"
            value={selected}
          />
        </LabeledInput>
      </Box>
    );
  },
};

export {
  Primary,
  Required,
  WithError,
  WithMultiSelect,
  WithSecondaryText,
  WithTextArea,
  WithTextAreaError,
};
export default StoryMeta;
